import { useQuery } from '@tanstack/react-query';
import { FileText, Loader2 } from 'lucide-react';
import { siteUser } from '@/api/client';
import { PageHeader } from '@/components/layout/PageHeader';

/**
 * F5 - /site-documentation
 *
 * Read-only whitelist for the `site_user` role. Lists doc types whose
 * catalog row has visible_to_site_users='true'. site_user is DENIED
 * everywhere else in eISF by the permission matrix; this is the only
 * doc surface they see.
 */
export function SiteDocumentationPage() {
  const q = useQuery({
    queryKey: ['site-user', 'site-documentation'],
    queryFn: () => siteUser.siteDocumentation(),
    staleTime: 60_000,
  });

  return (
    <div className="space-y-6">
      <PageHeader
        title="Site documentation"
        subtitle="Documents your Site Admin has published to all site staff. Contact your Site Admin if you need something that isn't listed here."
      />
      <div className="mx-auto max-w-4xl">

      {q.isLoading && (
        <div className="flex items-center gap-2 text-text-3 py-8 justify-center">
          <Loader2 className="animate-spin" size={16} /> Loading…
        </div>
      )}

      {q.isError && (
        <div className="rounded-sm border border-critical/40 bg-critical-soft p-4 text-[13px] text-critical">
          Could not load site documentation.
        </div>
      )}

      {q.data && q.data.count === 0 && (
        <div className="rounded-sm border border-border-strong border-dashed p-6 text-center text-text-3 text-[13px]">
          Your Site Admin has not published any documents to site users yet.
        </div>
      )}

      {q.data && q.data.count > 0 && (
        <div className="space-y-2">
          {q.data.document_types.map((d) => (
            <div key={d.document_type_code} className="flex items-start gap-3 bg-white border border-border-soft rounded-md shadow-card p-4">
              <FileText size={18} className="text-navy mt-1 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-[13.5px] text-text-1">{d.document_type_name}</div>
                <div className="text-[12px] text-text-3 mt-0.5 line-clamp-3">{d.definition}</div>
                <div className="text-[11px] text-text-4 mt-1 font-mono">
                  {d.document_type_code} · Lifecycle: {d.lifecycle}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      </div>
    </div>
  );
}
